'use client'

import React, { useState, useEffect, useCallback } from 'react';
import { Clock, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService, type AttemptInfo, type BestScore } from '@/services/api';

interface AttemptManagerProps {
    userId: string;
    playerName: string;
    team: 'julia' | 'benjamin';
    children: (canPlay: boolean, submitScores: (scores: any) => Promise<void>) => React.ReactNode;
}

/** -------- Modal d'affichage des résultats Training -------- */
type TrainingLine = { label: string; score: number; max: number };
type TrainingResults = {
    lines: TrainingLine[];
    total: number;
    max: number;
    percentage: number;
};

const TrainingResultsModal: React.FC<{
    open: boolean;
    onClose: () => void;
    results: TrainingResults | null;
}> = ({ open, onClose, results }) => {
    // close sur ESC
    React.useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    aria-modal="true"
                    role="dialog"
                    aria-labelledby="training-modal-title"
                    className="fixed inset-0 z-50 flex items-center justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={onClose}
                    />
                    {/* Dialog */}
                    <motion.div
                        className="relative z-10 w-[92vw] max-w-lg rounded-2xl bg-white p-6 shadow-2xl"
                        initial={{ scale: 0.98, y: 8, opacity: 0 }}
                        animate={{ scale: 1, y: 0, opacity: 1 }}
                        exit={{ scale: 0.98, y: 8, opacity: 0 }}
                    >
                        <button
                            onClick={onClose}
                            aria-label="Fermer"
                            className="absolute right-3 top-3 rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                        >
                            <X className="h-5 w-5" />
                        </button>

                        <h3 id="training-modal-title" className="mb-2 text-2xl font-bold text-bordeaux-700">
                            Récap
                        </h3>
                        <p className="mb-4 text-sm text-gray-600">
                            ⚠️ Ces scores ne sont pas sauvegardés.
                        </p>

                        {results && (
                            <>
                                <div className="mb-4 max-h-60 overflow-auto rounded-lg border border-gray-100 bg-gray-50">
                                    <ul className="divide-y divide-gray-100">
                                        {results.lines.map((l, idx) => (
                                            <li key={idx} className="flex items-center justify-between px-4 py-3">
                                                <span className="capitalize text-bordeaux-700">
                                                    {l.label.replace(/mini\s*jeu\s*(ben|benjamin|julia|julie)/i, 'Mini Jeu')}
                                                </span>
                                                <span className="font-semibold text-bordeaux-900">
                                                    {l.score}/{l.max}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="mb-6 rounded-xl border border-bordeaux-100 bg-white p-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-bordeaux-600">Score total</span>
                                        <span className="text-lg font-bold text-bordeaux-800">
                                            {results.total}/{results.max} ({results.percentage}%)
                                        </span>
                                    </div>
                                    <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                                        <div
                                            className="h-full rounded-full bg-gradient-to-r from-green-500 to-emerald-500"
                                            style={{ width: `${results.percentage}%` }}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={onClose}
                                className="flex-1 rounded-xl border border-gray-300 bg-white px-4 py-3 font-medium text-gray-700 hover:bg-gray-50"
                            >
                                Fermer
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
/** --------------------------------------------------------- */

const AttemptManager: React.FC<AttemptManagerProps> = ({
                                                           userId,
                                                           playerName,
                                                           team,
                                                           children
                                                       }) => {
    const [attemptInfo, setAttemptInfo] = useState<AttemptInfo | null>(null);
    const [bestScores, setBestScores] = useState<BestScore[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [trainingMode, setTrainingMode] = useState(false);

    // 🆕 état pour la modale training
    const [trainingResults, setTrainingResults] = useState<TrainingResults | null>(null);
    const [trainingModalOpen, setTrainingModalOpen] = useState(false);

    useEffect(() => {
        loadUserData();
    }, [userId, team]);

    const loadUserData = async () => {
        try {
            const [attemptsRes, scoresRes] = await Promise.all([
                apiService.checkUserAttempts(userId),
                apiService.getBestScores(userId)
            ]);

            if (attemptsRes.success) {
                setAttemptInfo(attemptsRes.data!);
            }

            if (scoresRes.success) {
                const filteredScores = (scoresRes.data || []).filter(score => {
                    if (score.game_type === 'quiz-general' || score.game_type === 'loverungame') return true;
                    if (team === 'julia' && score.game_type === 'mini-jeu-julia') return true;
                    if (team === 'benjamin' && score.game_type === 'mini-jeu-ben') return true;
                    return false;
                });

                // 🔥 NOUVELLE LOGIQUE : Filtrer pour ne garder que les meilleurs scores par game_type
                const uniqueBestScores = filteredScores.reduce((acc: BestScore[], current: BestScore) => {
                    const existingScore = acc.find(score => score.game_type === current.game_type);

                    if (!existingScore) {
                        // Premier score pour ce type de jeu
                        acc.push(current);
                    } else {
                        // Comparer les scores et garder le meilleur
                        if (current.best_score > existingScore.best_score ||
                            (current.best_score === existingScore.best_score && current.best_percentage > existingScore.best_percentage)) {
                            // Remplacer par le meilleur score
                            const index = acc.findIndex(score => score.game_type === current.game_type);
                            acc[index] = current;
                        }
                    }

                    return acc;
                }, []);

                setBestScores(uniqueBestScores);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des données:', error);
        } finally {
            setLoading(false);
        }
    };

    const submitScores = async (scores: Record<string, { score: number; maxScore: number }>) => {
        if (submitting || (!attemptInfo?.canPlay && !trainingMode)) return;

        setSubmitting(true);
        try {
            const results = Object.entries(scores).map(([gameType, data]) => ({
                game_type: gameType,
                score: data.score,
                max_score: data.maxScore
            }));

            const response = await apiService.submitGameResultsWithAttempts({
                userId,
                playerName,
                team,
                results
            });

            if (response.success) {
                await loadUserData();
            } else {
                throw new Error(response.message || 'Erreur lors de la sauvegarde');
            }
        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
            // on peut garder un toast ici si tu en as un
        } finally {
            setSubmitting(false);
        }
    };

// helper : autorise un type de jeu selon l'équipe
    const isAllowedForTeam = (gameType: string, team: 'julia' | 'benjamin') => {
        if (gameType === 'quiz-general' || gameType === 'loverungame') return true;
        if (team === 'julia' && gameType === 'mini-jeu-julia') return true;
        if (team === 'benjamin' && gameType === 'mini-jeu-ben') return true;
        return false;
    };

    const submitTrainingScores = async (
        scores: Record<string, { score: number; maxScore: number }>
    ) => {
        const entries = Object.entries(scores).filter(([gameType]) =>
            isAllowedForTeam(gameType, team)
        );

        const total = entries.reduce((sum, [, g]) => sum + g.score, 0);
        const max = entries.reduce((sum, [, g]) => sum + g.maxScore, 0);
        const percentage = max > 0 ? Math.round((total / max) * 100) : 0;

        const lines: TrainingLine[] = entries.map(([game, data]) => ({
            label: game.replace(/-/g, ' '),
            score: data.score,
            max: data.maxScore
        }));

        setTrainingResults({ lines, total, max, percentage });
        setTrainingModalOpen(true);
    };

    const closeTrainingModal = useCallback(() => {
        setTrainingModalOpen(false);
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-bordeaux-600"></div>
            </div>
        );
    }

    const canPlay = trainingMode || (attemptInfo?.canPlay ?? false);
    const submitFunction = trainingMode ? submitTrainingScores : submitScores;

    if (!canPlay) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
            >
                <div className="card">
                    <div className="text-center">
                        <h3 className="mb-2 text-2xl font-bold text-amber-500">
                            Tentatives épuisées
                        </h3>
                        <p className="mb-2 text-sm text-bordeaux-600">Vos meilleurs scores :</p>

                        {bestScores.length > 0 ? (
                            <div className="mb-6 rounded-lg bg-white/80 p-4">
                                {bestScores.map((score, index) => (
                                    <div key={`${score.game_type}-${index}`} className="flex items-center justify-between border-b border-gray-100 p-3 last:border-b-0">
                                        <span className="capitalize font-medium text-bordeaux-700">
                                          {score.game_type.replace(/-/g, ' ')}
                                        </span>
                                        <div className="text-right">
                                            <div className="text-lg font-bold text-bordeaux-800">
                                                {score.best_score}/{score.max_score}
                                            </div>
                                            <div className="text-sm text-bordeaux-600">{score.best_percentage}%</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-8 text-center text-bordeaux-500">Aucun score enregistré</div>
                        )}

                        <motion.button
                            onClick={() => setTrainingMode(true)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="mb-4 w-full rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 px-6 py-4 text-lg font-semibold text-white shadow-lg transition-all duration-200 hover:shadow-xl"
                        >
                            Mode entraînement
                        </motion.button>

                        <p className="text-xs text-gray-500">
                            Jouez sans limite, mais les scores ne sont pas sauvegardés !
                        </p>
                    </div>
                </div>
            </motion.div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Bandeau mode training */}
            {trainingMode && (
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="card border-green-200 bg-gradient-to-r from-green-50 to-emerald-50"
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="font-semibold text-green-700">Jouez sans limite !</h4>
                            <p className="text-sm text-green-600">Les scores ne sont pas sauvegardés.</p>
                        </div>
                        <button
                            onClick={() => setTrainingMode(false)}
                            className="flex items-center gap-2 text-xs text-green-700 hover:text-bordeaux-800 transition-colors bg-bordeaux-50 hover:bg-bordeaux-100 px-4 py-2 rounded-lg"
                        >
                            Quitter le mode training
                        </button>
                    </div>
                </motion.div>
            )}

            {/* Indicateur tentatives */}
            {!trainingMode && attemptInfo && (
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`card ${
                        attemptInfo.remainingAttempts === 1
                            ? 'border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50'
                            : 'border-green-200 bg-gradient-to-r from-green-50 to-emerald-50'
                    }`}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <Clock
                                className={`mr-3 h-6 w-6 ${
                                    attemptInfo.remainingAttempts === 1 ? 'text-amber-600' : 'text-green-600'
                                }`}
                            />
                            <div>
                                <h4 className="font-semibold text-bordeaux-700">
                                    {attemptInfo.remainingAttempts === 2 ? 'Première tentative' : 'Dernière tentative !'}
                                </h4>
                            </div>
                        </div>
                    </div>

                    {attemptInfo.remainingAttempts === 1 && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="mt-4 rounded-lg border border-amber-300 bg-amber-100 p-3"
                        >
                            <p className="text-sm font-medium text-amber-800">
                                ⚠️ Attention : C'est votre dernière tentative ! Seuls vos meilleurs scores seront conservés.
                            </p>
                        </motion.div>
                    )}
                </motion.div>
            )}

            {children(canPlay, submitFunction)}

            <TrainingResultsModal
                open={trainingModalOpen}
                onClose={closeTrainingModal}
                results={trainingResults}
            />
        </div>
    );
};

export default AttemptManager;
